import React, { useEffect, useRef } from "react";
import { Instagram } from "lucide-react";
import { CornerOrnament, Paisley, BorderOrnament } from "./IndianMotifs";

export default function About() {
  const img = useRef(null);
  const root = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      if (!img.current || !root.current) return;
      const r = root.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = Math.max(-1, Math.min(1, (r.top + r.height / 2 - vh / 2) / vh));
      img.current.style.transform = `translateY(${-p * 40}px)`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section id="about" ref={root} className="relative py-28 md:py-40 overflow-hidden">
      <CornerOrnament size={140} className="absolute top-10 left-10 text-sage-700/15 hidden md:block" />
      <CornerOrnament size={140} rotate={90} className="absolute top-10 right-10 text-sage-700/15 hidden md:block" />
      <Paisley size={140} className="absolute bottom-20 right-[12%] text-gold-500/15 hidden lg:block" />
      <div className="container mx-auto px-5 md:px-10">
        <div className="flex flex-col gap-12">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.32em] text-sage-700/80 mb-4">Our Dentists — 01</div>
            <h2 className="font-display text-5xl md:text-6xl leading-[1.02] tracking-tightest text-ink">
              Two pairs of careful hands.<br />
              <span className="font-script text-gold-500">One shared promise.</span>
            </h2>
            <div className="mt-5 text-gold-600/70"><BorderOrnament width={200} /></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="glass rounded-[32px] p-8 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
                <Paisley size={80} className="text-gold-500" />
              </div>
              <h3 className="font-display text-3xl text-sage-900">Dr. SN</h3>
              <div className="text-[10px] uppercase tracking-[0.2em] text-sage-700/60 mb-4">(Dentist)</div>
              <p className="text-ink/70 leading-relaxed min-h-[80px]">
                A gentle, attentive practitioner with a passion for cosmetic and restorative care. Dr. SN brings calm to every chair.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {["Gentle care", "Cosmetic dentistry", "Family care"].map((tag) => (
                  <span key={tag} className="text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full border border-sage-700/20 text-sage-800 bg-white/50">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="glass rounded-[32px] p-8 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
                <Paisley size={80} className="text-gold-500" />
              </div>
              <h3 className="font-display text-3xl text-sage-900">Dr. KB</h3>
              <div className="text-[10px] uppercase tracking-[0.2em] text-sage-700/60 mb-4">(Dentist)</div>
              <p className="text-ink/70 leading-relaxed min-h-[80px]">
                Detail-oriented and warm, Dr. KB pairs modern technique with a quiet, patient-first chairside manner.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {["Modern technology", "Restorative", "Healthy smiles"].map((tag) => (
                  <span key={tag} className="text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full border border-sage-700/20 text-sage-800 bg-white/50">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-start">
            <a
              href="https://instagram.com/dentist"
              target="_blank"
              rel="noreferrer"
              data-testid="about-instagram"
              data-cursor="hover"
              className="group mt-4 inline-flex items-center gap-4 rounded-full border border-sage-700/15 bg-white/70 backdrop-blur px-3 py-2.5 pr-6 ring-gold-soft tilt-card"
            >
              <span className="grid place-items-center w-11 h-11 rounded-full bg-gradient-to-br from-gold-400 to-sage-700 text-cream group-hover:rotate-12 transition-transform duration-500">
                <Instagram className="w-5 h-5" strokeWidth={1.6} />
              </span>
              <span className="flex flex-col leading-tight">
                <span className="text-[10px] uppercase tracking-[0.28em] text-sage-700/70">Follow our work</span>
                <span className="font-display text-lg text-sage-900">@dentist</span>
              </span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
